/** The two hooks every view uses: one read, and one read-then-reload write. */
import { useCallback, useEffect, useState } from "react"
import { startAnalytics, trackPage } from "./analytics"
import { ApiError } from "./api"

/**
 * Data the prerenderer already fetched, keyed by whatever the page asked for.
 *
 * Without this, every data-driven GTM page prerendered to a single "Loading…" — a crawler read no
 * incidents, no profiles, no taxonomy and no figures, which defeats the entire reason GTM is not a
 * single-page app. The build fetches, renders with real content, and serializes what it used into
 * the document; the browser starts from the same data rather than flashing a spinner over content
 * that is already on screen.
 *
 * It is a *starting point*, not a cache: `useAsync` still fetches, so a page served from a CDN an
 * hour later corrects itself. Stale-but-present beats absent, and both beat a spinner.
 */
type Preload = Record<string, unknown>

declare global {
  interface Window { __AGITOME_PRELOAD__?: Preload }
}

const PRELOAD: Preload =
  (typeof window !== "undefined" && window.__AGITOME_PRELOAD__) || {}

/** Set by the prerenderer before it renders a route. */
export function seedPreload(data: Preload): void {
  for (const [k, v] of Object.entries(data)) PRELOAD[k] = v
}

export function preloaded<T>(key: string | undefined): T | null {
  return key && key in PRELOAD ? (PRELOAD[key] as T) : null
}

export interface AsyncState<T> {
  data: T | null
  error: string | null
  loading: boolean
}

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return String(err)
}

/**
 * Fetch on mount and whenever `deps` change.
 *
 * Returns `reload` rather than a setter, deliberately. Views call it after a write and re-render
 * from what the API actually returned — the console never optimistically invents state it has not
 * read back. That property is exactly what the e2e suite asserts: act in the UI, then confirm the
 * backend agrees.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[] = [],
  /** Names this read so the prerenderer's result for it can be the starting state. */
  preloadKey?: string,
): AsyncState<T> & { reload: () => void } {
  const seeded = preloaded<T>(preloadKey)
  const [state, setState] = useState<AsyncState<T>>(
    seeded !== null
      ? { data: seeded, error: null, loading: false }
      : { data: null, error: null, loading: true },
  )
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let live = true
    // Keep whatever is on screen while refetching. A page that blanked back to "Loading…" over
    // content the reader can already see is a worse page than one that updates in place.
    setState((s) => ({ ...s, loading: s.data === null, error: null }))
    fn()
      .then((data) => live && setState({ data, error: null, loading: false }))
      .catch((err) => live && setState({ data: null, error: message(err), loading: false }))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { ...state, reload: useCallback(() => setNonce((n) => n + 1), []) }
}

/** A write, with its own pending/error state, that reloads the reader when it lands. */
export function useAction(reload?: () => void) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setPending(true)
      setError(null)
      try {
        await fn()
        reload?.()
      } catch (err) {
        setError(message(err))
      } finally {
        setPending(false)
      }
    },
    [reload],
  )

  return { run, pending, error }
}

/** Hash routing. Small enough to own: the alternative is a router dependency for four surfaces
 *  whose deepest route is two segments. */
export function useRoute(): [string, (to: string) => void] {
  const [route, setRoute] = useState(() => window.location.hash.slice(1) || "/")
  useEffect(() => {
    const onHash = () => setRoute(window.location.hash.slice(1) || "/")
    window.addEventListener("hashchange", onHash)
    return () => window.removeEventListener("hashchange", onHash)
  }, [])
  return [route, (to: string) => (window.location.hash = to)]
}

/**
 * Start analytics, and record a view whenever the route changes.
 *
 * TAKES THE RESOLVED ROUTE, not `location.pathname`. An SPA's automatic pageview fires once on
 * load, before the router has decided anything, so every view in the session is attributed to
 * the URL the visitor entered on — and "which page do people read" then answers "the one they
 * landed on", every time, which looks plausible and is useless.
 */
export function usePageViews(path: string, { cookieless = true }: { cookieless?: boolean } = {}) {
  useEffect(() => {
    startAnalytics({ cookieless })
  }, [cookieless])
  useEffect(() => {
    trackPage(path)
  }, [path])
}
